import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, RefObject } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api'
import type { FeedbackView, IconicChat, IconicTurn, Me } from '../types'
import { stageOf, decisionWords } from '../lib/feedback-view'
import {
  chipsFor, placeholderFor, footnoteFor, groupChats, isOld, oldBanner, questionFor, verbFor, cardShows, pieces,
  dayWords, POLL_MS, LOST_AFTER, LOST, NOT_CONFIRMED, DELETE_CONFIRM, REMATCH_MS, withoutDeleted,
} from '../lib/iconic-view'
import type { Remembered } from '../lib/iconic-view'
import {
  HistoryIcon, PlusIcon, MinimiseIcon, MaximiseIcon, RestoreIcon, CloseIcon, PencilIcon, TrashIcon,
  SendIcon, StopIcon, CheckIcon,
} from './icons'

interface Props {
  me: Me
  screen: string
  record?: string
  rem: Remembered
  setRem: (r: Remembered | ((r: Remembered) => Remembered)) => void
  /** The topbar button, so focus goes back to it when the panel closes. */
  opener: RefObject<HTMLButtonElement>
}

const messageOf = (err: unknown) => (err instanceof Error ? err.message : 'That did not work.')

/**
 * Iconic, beside the page (STE-997).
 *
 * The page behind stays usable. Every question is saved under the person, the
 * answer is checked on every three seconds while it is worked on, and nothing
 * Iconic proposes happens until OK is pressed on its card.
 */
export function IconicPanel({ me, screen, record, rem, setRem, opener }: Props) {
  const [chats, setChats] = useState<IconicChat[] | null>(null)
  const [q, setQ] = useState('')
  const [chat, setChat] = useState<IconicChat | null>(null)
  // The remembered chat is still on its way: nothing is drawn as if it were a new one.
  const [opening, setOpening] = useState(() => rem.chatId !== null)
  const [turns, setTurns] = useState<IconicTurn[]>([])
  const [text, setText] = useState('')
  const [problem, setProblem] = useState('')
  const [lost, setLost] = useState(false)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [sending, setSending] = useState(false)
  const [now, setNow] = useState(() => new Date())
  const box = useRef<HTMLTextAreaElement>(null)
  const bottom = useRef<HTMLDivElement>(null)
  const misses = useRef(0)
  /** The chat whose turns are on screen, so starting one does not then read it back over the question just sent. */
  const shown = useRef<string | null>(null)

  const running = turns.find((t) => t.kind === 'answer' && (t.state === 'queued' || t.state === 'thinking'))
  const newestAnswer = [...turns].reverse().find((t) => t.kind === 'answer')
  const set = (patch: Partial<Remembered>) => setRem((r) => ({ ...r, ...patch }))

  /* ---------- loading ---------- */

  // Chats deleted here. The list is read from an index that trails the table by
  // a moment, so a reload just after a delete can still return the chat (FR-IC40).
  const gone = useRef<Set<string>>(new Set())

  const loadChats = useCallback(async (search: string) => {
    try {
      const list = withoutDeleted((await api.iconicChats(search)).chats, gone.current)
      setChats(list)
      // The server names a chat from its first question, so the header follows the list.
      setChat((c) => (c ? list.find((x) => x.chatId === c.chatId) ?? c : c))
    } catch { setChats((c) => c ?? []) /* the list is a convenience; the chat still works */ }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => void loadChats(q), 250)
    return () => clearTimeout(t)
  }, [q, loadChats])

  const openChat = async (chatId: string | null) => {
    if (chatId !== null && chatId === shown.current) return
    setProblem(''); setLost(false); setDeleting(false); setRenaming(null)
    shown.current = chatId
    if (!chatId) { setChat(null); setTurns([]); setOpening(false); return }
    setOpening(true)
    try {
      const view = await api.iconicChat(chatId)
      if (shown.current !== chatId) return
      setChat(view.chat); setTurns(view.turns)
    } catch {
      if (shown.current !== chatId) return
      // Deleted in another tab, or never there: start clean rather than show an error.
      shown.current = null
      setChat(null); setTurns([])
      setRem((r) => (r.chatId === chatId ? { ...r, chatId: null } : r))
    } finally {
      if (shown.current === chatId || shown.current === null) setOpening(false)
    }
  }

  // The open chat survives a reload.
  useEffect(() => { void openChat(rem.chatId) }, [rem.chatId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Focus goes into the panel when it opens.
  useEffect(() => { box.current?.focus() }, [])

  // Only the messages scroll: scrollIntoView would also move the page behind the panel.
  useEffect(() => {
    const list = bottom.current?.parentElement
    if (list) list.scrollTop = list.scrollHeight
  }, [turns.length, running?.state])

  // "Today" moves on at midnight, for the dated banners and the list's groups.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(t)
  }, [])

  /* ---------- watching an answer ---------- */

  useEffect(() => {
    if (!running || lost) return
    let live = true
    const t = setInterval(async () => {
      try {
        const turn = await api.iconicTurn(running.turnId)
        if (!live) return
        misses.current = 0
        setTurns((old) => old.map((x) => (x.turnId === turn.turnId ? turn : x)))
        if (turn.state !== 'queued' && turn.state !== 'thinking') void loadChats(q)
      } catch {
        misses.current += 1
        if (misses.current >= LOST_AFTER) setLost(true)
      }
    }, POLL_MS)
    return () => { live = false; clearInterval(t) }
  }, [running?.turnId, running?.state, lost]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- asking ---------- */

  const ask = async (question: string) => {
    const body = question.trim()
    if (!body || running || sending || opening) return
    setSending(true); setProblem('')
    try {
      let current = chat
      if (!current) {
        current = await api.startIconicChat()
        shown.current = current.chatId
        setChat(current)
        set({ chatId: current.chatId })
      }
      const { turnId } = await api.askIconic(current.chatId, { text: body, screen, ...(record ? { record } : {}) })
      setText('')
      // Shown at once, before any answer exists.
      const seq = Number(turnId.split('~')[1])
      const at = new Date().toISOString()
      setTurns((old) => [
        ...old,
        { turnId: `${current!.chatId}~${seq - 1}`, seq: seq - 1, kind: 'question', text: body, at, screen },
        { turnId, seq, kind: 'answer', text: '', at, state: 'queued', screen },
      ])
      misses.current = 0
      void loadChats(q)
    } catch (err) {
      // The server's own words: still answering, the daily limit, too long.
      setProblem(messageOf(err))
    } finally {
      setSending(false)
    }
  }

  const stop = async () => {
    if (!running) return
    try {
      const turn = await api.stopIconicTurn(running.turnId)
      setTurns((old) => old.map((x) => (x.turnId === turn.turnId ? turn : x)))
    } catch (err) { setProblem(messageOf(err)) }
  }

  const act = async (turn: IconicTurn, verb: 'confirm' | 'cancel') => {
    if (!turn.action) return
    try {
      const next = verb === 'confirm'
        ? await api.confirmIconicAction(turn.turnId, turn.action.actionId)
        : await api.cancelIconicAction(turn.turnId, turn.action.actionId)
      setTurns((old) => old.map((x) => (x.turnId === next.turnId ? next : x)))
    } catch {
      // Refused (too old, or not the newest answer): read back what the server now holds.
      try {
        const back = await api.iconicTurn(turn.turnId)
        setTurns((old) => old.map((x) => (x.turnId === back.turnId ? back : x)))
      } catch { /* leave it as it was */ }
    }
  }

  /* ---------- the chat itself ---------- */

  const rename = async () => {
    if (!chat || renaming === null) return
    try {
      const next = await api.renameIconicChat(chat.chatId, renaming)
      setChat(next); setRenaming(null); void loadChats(q)
    } catch (err) { setProblem(messageOf(err)) }
  }

  const remove = async () => {
    if (!chat) return
    try {
      await api.deleteIconicChat(chat.chatId)
      gone.current.add(chat.chatId)
      setChats((c) => (c ? withoutDeleted(c, gone.current) : c))
      setDeleting(false)
      shown.current = null
      set({ chatId: null })
      setChat(null); setTurns([])
      void loadChats(q)
    } catch (err) { setProblem(messageOf(err)) }
  }

  const close = () => { set({ open: false }); opener.current?.focus() }
  const minimise = () => set({ size: 'min', wasSize: rem.size === 'max' ? 'max' : 'side' })

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); minimise() }
  }

  if (rem.size === 'min') {
    return (
      <button type="button" className="iconic-pill" onClick={() => set({ size: rem.wasSize })}
        aria-label={`Open Iconic${chat ? `: ${chat.title}` : ''}`}>
        <span className="iconic-pill__title">{chat ? chat.title : 'Iconic'}</span>
      </button>
    )
  }

  const chips = chipsFor(me.role, screen, record)
  const groups = groupChats(chats ?? [], now)

  return (
    <aside className={`iconic iconic--${rem.size}${rem.listShown ? ' iconic--list' : ''}`} role="complementary"
      aria-label="Iconic" onKeyDown={onKey}>
      {rem.listShown && (
        <div className="iconic-list">
          <div className="iconic-list__head">
            <b>Your chats</b>
            <button type="button" className="btn btn--sm" onClick={() => { set({ chatId: null }); void openChat(null) }}>New chat</button>
          </div>
          <input className="iconic-list__search" type="search" placeholder="Search your chats" aria-label="Search your chats"
            value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="iconic-list__body">
            {chats !== null && groups.length === 0 && q.trim() !== '' && <p className="iconic-list__none">No chats match ‘{q.trim()}’</p>}
            {chats !== null && groups.length === 0 && q.trim() === '' && <p className="iconic-list__none">Your chats will be kept here.</p>}
            {groups.map((g) => (
              <div key={g.group} className="iconic-list__group">
                <h3>{g.group}</h3>
                {g.chats.map((c) => (
                  <button key={c.chatId} type="button" className="iconic-list__item"
                    aria-current={chat?.chatId === c.chatId ? 'true' : undefined}
                    // On a phone the list covers the chat, so picking one goes into it.
                    onClick={() => set({ chatId: c.chatId, ...(window.matchMedia?.('(max-width: 760px)').matches ? { listShown: false } : {}) })}>
                    {c.title}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      <section className="iconic-chat">
        <header className="iconic-head">
          <button type="button" className="icon-btn" onClick={() => set({ listShown: !rem.listShown })}
            aria-label={rem.listShown ? 'Hide chat history' : 'Show chat history'} aria-pressed={rem.listShown} title="Past chats">
            <HistoryIcon />
          </button>
          {!rem.listShown && (
            <button type="button" className="icon-btn" onClick={() => { set({ chatId: null }); void openChat(null) }} aria-label="New chat" title="New chat">
              <PlusIcon />
            </button>
          )}
          <div className="iconic-head__text">
            {renaming !== null ? (
              <form onSubmit={(e) => { e.preventDefault(); void rename() }}>
                <input className="iconic-head__rename" value={renaming} maxLength={80} aria-label="Chat name" autoFocus
                  onChange={(e) => setRenaming(e.target.value)} onBlur={() => void rename()}
                  onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setRenaming(null) } }} />
              </form>
            ) : (
              <b title={chat?.title}>{chat ? chat.title : 'Iconic'}</b>
            )}
            <span>{chat ? `${dayWords(chat.startedAt)} · only you` : `Your AI agent · on ${screen}`}</span>
          </div>
          <button type="button" className="icon-btn" onClick={minimise} aria-label="Minimise Iconic" title="Minimise"><MinimiseIcon /></button>
          {rem.size === 'max' ? (
            <button type="button" className="icon-btn" onClick={() => set({ size: 'side' })} aria-label="Restore Iconic to the side" title="Restore"><RestoreIcon /></button>
          ) : (
            <button type="button" className="icon-btn" onClick={() => set({ size: 'max' })} aria-label="Maximise Iconic" title="Maximise"><MaximiseIcon /></button>
          )}
          {chat && (
            <>
              <button type="button" className="icon-btn" onClick={() => setRenaming(chat.title)} aria-label="Rename chat" title="Rename chat"><PencilIcon /></button>
              <button type="button" className="icon-btn" onClick={() => setDeleting(true)} aria-label="Delete chat" title="Delete chat"><TrashIcon /></button>
            </>
          )}
          <button type="button" className="icon-btn" onClick={close} aria-label="Close Iconic" title="Close"><CloseIcon /></button>
        </header>

        {deleting && (
          <div className="iconic-confirm" role="alertdialog" aria-label="Delete this chat">
            <p>{DELETE_CONFIRM}</p>
            <div className="iconic-confirm__actions">
              <button type="button" className="btn btn--sm" onClick={() => setDeleting(false)}>Keep it</button>
              <button type="button" className="btn btn--sm btn--danger" onClick={() => void remove()}>Delete chat</button>
            </div>
          </div>
        )}

        <div className="iconic-msgs" aria-live="polite">
          {opening && turns.length === 0 && <p className="iconic-hello">Opening your chat…</p>}
          {!opening && turns.length === 0 && (
            <p className="iconic-hello">
              {me.role === 'manager'
                ? 'Ask me about a role, its past versions, or what you have asked HR for.'
                : 'Ask me about your roles, market data, benchmarks and ranges, or tell me what to do.'}
            </p>
          )}
          {turns.map((t, i) => {
            const day = i === 0 || dayWords(turns[i - 1].at) !== dayWords(t.at)
            return (
              <div key={t.turnId}>
                {day && <div className="iconic-day"><span>{new Date(t.at).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</span></div>}
                {t.kind === 'question'
                  ? <div className="iconic-q">{t.text}</div>
                  : <Answer turn={t} turns={turns} now={now} newest={t.turnId === newestAnswer?.turnId} lost={lost && t.turnId === running?.turnId}
                      onRetry={() => { misses.current = 0; setLost(false) }}
                      onAskAgain={(question) => void ask(question)} onAct={(verb) => void act(t, verb)} />}
              </div>
            )
          })}
          <div ref={bottom} />
        </div>

        {!running && !opening && chips.length > 0 && (
          <div className="iconic-chips">
            {chips.map((c) => <button key={c} type="button" className="iconic-chip" onClick={() => void ask(c)}>{c}</button>)}
          </div>
        )}

        <form className="iconic-ask" onSubmit={(e) => { e.preventDefault(); void ask(text) }}>
          {problem && <p className="iconic-ask__problem" role="alert">{problem}</p>}
          <div className="iconic-ask__box">
            <textarea ref={box} rows={2} value={text} maxLength={8000}
              placeholder={chat && turns.length ? 'Keep asking in this chat' : placeholderFor(me.role)}
              aria-label={placeholderFor(me.role)}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void ask(text) } }} />
            {running ? (
              <button type="button" className="iconic-send iconic-send--stop" onClick={() => void stop()} aria-label="Stop Iconic"><StopIcon /></button>
            ) : (
              <button type="submit" className="iconic-send" disabled={!text.trim() || sending || opening} aria-label="Send"><SendIcon /></button>
            )}
          </div>
          <p className="iconic-foot">{chat && turns.length ? 'Deleting a chat does not undo what was changed. That stays in the product’s own history.' : footnoteFor(me.role)}</p>
        </form>
      </section>
    </aside>
  )
}

function Answer({ turn, turns, now, newest, lost, onRetry, onAskAgain, onAct }: {
  turn: IconicTurn; turns: IconicTurn[]; now: Date; newest: boolean; lost: boolean
  onRetry: () => void; onAskAgain: (q: string) => void; onAct: (verb: 'confirm' | 'cancel') => void
}) {
  const question = questionFor(turns, turn)
  if (turn.state === 'queued' || turn.state === 'thinking') {
    return (
      <div className="iconic-a">
        {lost ? (
          <div className="iconic-a__line iconic-a__line--muted">
            {LOST} <button type="button" className="iconic-link" onClick={onRetry}>Retry</button>
          </div>
        ) : (
          <div className="iconic-a__line iconic-thinking">Thinking{turn.doing ? <span> · {turn.doing}</span> : null}</div>
        )}
      </div>
    )
  }
  if (turn.state === 'failed' || turn.state === 'stopped') {
    return (
      <div className="iconic-a">
        <div className="iconic-a__line iconic-a__line--muted">
          {turn.state === 'stopped' ? 'Stopped. Nothing was changed.' : turn.failedReason || 'Iconic could not finish this answer.'}
          {question && <> <button type="button" className="iconic-link" onClick={() => onAskAgain(question)}>Ask again</button></>}
        </div>
      </div>
    )
  }
  const a = turn.action
  const shows = a ? cardShows(a, newest, now.getTime()) : null
  return (
    <div className="iconic-a">
      <div className="iconic-a__body">
        {turn.text && (
          <div className="iconic-a__text">
            {pieces(turn.text).map((line, i) => (
              <p key={i}>{line.map((p, j) => (p.bold ? <b key={j}>{p.text}</b> : <span key={j}>{p.text}</span>))}</p>
            ))}
          </div>
        )}
        {turn.answeredAt && isOld(turn.answeredAt, now) && (
          <div className="iconic-old">
            <p>{oldBanner(turn.answeredAt)}</p>
            {question && <button type="button" className="btn btn--sm" onClick={() => onAskAgain(question)}>Ask again with today’s data</button>}
          </div>
        )}
        {a && shows === 'waiting' && (
          <div className="iconic-card">
            <div className="iconic-card__label">Waiting for your OK</div>
            <div className="iconic-card__body">
              <b>{a.summary[0]}</b>
              {a.summary.length > 1 && <ul>{a.summary.slice(1).map((l, i) => <li key={i}>{l}</li>)}</ul>}
              {a.warning && <p className="iconic-card__warn">{a.warning}</p>}
            </div>
            <div className="iconic-card__actions">
              <button type="button" className="btn btn--sm" onClick={() => onAct('cancel')}>Cancel</button>
              <button type="button" className="btn btn--sm btn--primary" onClick={() => onAct('confirm')}>{verbFor(a.kind)}</button>
            </div>
          </div>
        )}
        {a && shows === 'running' && <div className="iconic-result">Working on it…</div>}
        {a && shows === 'done' && (
          <div className="iconic-result iconic-result--done">
            <CheckIcon size={15} />
            <span><b>Done.</b> {a.summary[0].replace(/:\s*$/, '.')}</span>
            {a.link && <Link to={a.link}>Open</Link>}
          </div>
        )}
        {a && shows === 'done' && a.kind === 'proposeDisagree' && rematchRole(a.link) && (
          <RematchStatus jdId={rematchRole(a.link)!} title={titleIn(a.summary[0])} onAsk={onAskAgain} />
        )}
        {a && shows === 'failed' && (
          <div className="iconic-result iconic-result--failed"><span><b>Not done.</b> {a.result}</span></div>
        )}
        {a && shows === 'cancelled' && <div className="iconic-result"><span><b>Cancelled.</b> Nothing was changed.</span></div>}
        {a && shows === 'notDone' && <div className="iconic-result"><span>{NOT_CONFIRMED}</span></div>}
      </div>
    </div>
  )
}

/** The role a re-match is about, from the action's link to its benchmark. */
const rematchRole = (link?: string) => /^\/benchmarking\/([A-Za-z0-9_-]+)$/.exec(link ?? '')?.[1]
const titleIn = (line: string) => /“([^”]+)”/.exec(line)?.[1] ?? 'this role'

/**
 * Where a re-match HR asked for stands, under its Done line (STE-992 in the chat).
 *
 * Checked every five seconds while it runs and not after, so the chat never
 * goes quiet while something is still happening, and never polls for ever.
 * The decision is still Iconic's card and the person's OK: the buttons ask.
 */
function RematchStatus({ jdId, title, onAsk }: { jdId: string; title: string; onAsk: (q: string) => void }) {
  const [view, setView] = useState<FeedbackView | null>(null)
  const [lost, setLost] = useState(false)
  const stage = stageOf(view)
  useEffect(() => {
    let live = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const look = async () => {
      try {
        const v = await api.getFeedback(jdId)
        if (!live) return
        setView(v); setLost(false)
        if (stageOf(v) === 'running') timer = setTimeout(look, REMATCH_MS)
      } catch {
        if (live) setLost(true)
      }
    }
    void look()
    return () => { live = false; if (timer) clearTimeout(timer) }
  }, [jdId])

  if (lost) return <div className="iconic-status">Could not check the re-match. <Link to={`/benchmarking/${jdId}`}>Open the benchmark</Link></div>
  if (!view) return <div className="iconic-status">Checking the re-match…</div>
  if (stage === 'running') return <div className="iconic-status iconic-status--live" role="status">Re-matching “{title}”. This usually takes under a minute.</div>
  if (stage === 'ready') {
    return (
      <div className="iconic-status iconic-status--ready" role="status">
        <p><b>The re-match is ready.</b> Keep the new match, or the current one? Nothing changes until you choose.</p>
        <div className="iconic-status__actions">
          <button type="button" className="iconic-chip" onClick={() => onAsk(`What did the re-match for ${title} come back with, next to the current match?`)}>Compare them</button>
          <button type="button" className="iconic-chip" onClick={() => onAsk(`Keep the new match for ${title}.`)}>Keep the new match</button>
          <button type="button" className="iconic-chip" onClick={() => onAsk(`Keep the current match for ${title}.`)}>Keep the current one</button>
        </div>
      </div>
    )
  }
  if (stage === 'failed') return <div className="iconic-status">The re-match could not be finished. Nothing changed.</div>
  if (stage === 'stale') return <div className="iconic-status">The role or its survey changed while it ran, so this re-match can no longer be kept. Ask again to re-match it now.</div>
  const last = view.history[0]
  return <div className="iconic-status">{last ? `Decided: ${decisionWords[last.decision]}.` : 'Nothing is waiting on this role.'}</div>
}
